/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {error} from '@ion/core/src/logger/logger';
import {useKeyPress} from '../../hooks/useKeyPress';
import {dlsColor} from '../../theme/theme';
import {isCurrentTheme} from '../../theme/useThemeContext';
import initializeDrawingTools from '../helpers/initializeDrawingTools';
import {removeActiveFence} from '../helpers/MapViewUtils';
import {DEFAULT_SYMBOL} from './constants';
import {
  GEOFENCE_DRAW_SHAPE_CLASS,
  getActivePointSymbol,
  isLegacyMarker,
  registerCurrentDrawingUpdates,
  startGeofenceDrawingSession
} from './drawingStyles';

// defines the prop definitions for the CreateShapesBoundaries
export type CreateShapesBoundariesProps = {
  map: __esri.Map,
  mapView: __esri.MapView,
  radius?: number;
  setDrawingComplete?: (val: boolean) => void;
  setFinalDrawingDetails?: (val: {rings: Array<any>}) => void;
  finishedDrawing?: boolean;
  isFMTracking?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  cancelDrawing?: Function;
  editGeofenceGraphic?: __esri.Graphic;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
};

/**
 * Create default Symbol for editing Geofence
 */
const DEFAULT_EDIT_SYMBOL = {...DEFAULT_SYMBOL(), style: 'solid'};

/**
 * Provides Drawing shapes & boundaries functionality
 *
 * ```typescript
 *
 *    <CreateShapesBoundaries map={map} mapView={mapView} />
 *
 * ```
 */
export const CreateShapesBoundaries = ({
  map,
  mapView,
  setDrawingComplete,
  setFinalDrawingDetails,
  finishedDrawing,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  editGeofenceGraphic,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  isFMTracking = false
}: CreateShapesBoundariesProps) => {
  const deleteKey             = useKeyPress('Delete');
  const escapeKey             = useKeyPress('Escape');
  const drawActionRef         = useRef(null);
  const doubleClickHandlerRef = useRef(null);

  useEffect(() => {
    initializeDrawing();
    const endDrawingSession = isCurrentTheme() ? startGeofenceDrawingSession(mapViewEl) : null;
    return () => {
      endDrawingSession?.();
      mapViewEl?.classList?.add('default-cursor');
      mapViewEl?.classList?.remove('crosshair-only');
      mapViewEl?.classList?.remove('stop-drawing');
      mapViewEl?.classList?.remove(GEOFENCE_DRAW_SHAPE_CLASS);
      drawActionRef.current?.destroy?.();
      doubleClickHandlerRef.current?.remove?.();
    };
  }, []);

  useEffect(() => {
    return function() {
      if (geoFencesGraphicsLayerRef.current) {
        // sketchViewModelRef.current?.complete();
        sketchViewModelRef.current?.cancel();
        map.remove(geoFencesGraphicsLayerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deleteKey || escapeKey) {
      if (geoFencesGraphicsLayerRef.current) {
        // If the user is currently manipulating a drawing
        // graphic delete it and re-initialize the drawing tool
        removeActiveFence(map, [geoFencesGraphicsLayerRef, sketchViewModelRef]);
        cancelDrawing();
        setDrawingComplete(false);
        mapClickHandler?.remove();
      }
    }
  }, [deleteKey, escapeKey]);

  useEffect(() => {
    if (finishedDrawing) {
      const items = geoFencesGraphicsLayerRef.current?.graphics?.items;
      if (items?.length && items[0].geometry?.rings) {
        setFinalDrawingDetails(items[0].geometry.rings[0]);
      }
    };
  }, [finishedDrawing]);

  async function initializeDrawing() {
    const [
      Draw,
      Graphic,
      GraphicsLayer,
      SketchViewModel
    ] = await initializeDrawingTools(isFMTracking);
    const isCurrent                   = isCurrentTheme();
    geoFencesGraphicsLayerRef.current = new GraphicsLayer();
    map.layers.add(geoFencesGraphicsLayerRef.current);
    map.layers.reorder(geoFencesGraphicsLayerRef.current, 1);
    mapViewEl?.classList?.add('crosshair');
    mapViewEl?.classList?.remove('default-cursor');

    sketchViewModelRef.current = new SketchViewModel({
      view                 : mapView,
      layer                : geoFencesGraphicsLayerRef.current,
      updateOnGraphicClick : editGeofenceGraphic ?? false,
      defaultUpdateOptions : {
        multipleSelectionEnabled : false,
        preserveAspectRatio      : true,
        enableRotation           : false,
        toggleToolOnClick        : editGeofenceGraphic ?? false
      },
      polygonSymbol : DEFAULT_EDIT_SYMBOL
    });
    const circleSymbol = {
      type    : 'simple-marker',
      style   : 'circle',
      size    : 8,
      color   : dlsColor('white'),
      outline : {
        color : dlsColor('skyBlue'),
        width : 2
      }
    };

    const polygonSymbol = {
      type    : 'simple-fill',
      color   : [150, 150, 150, 0.2],
      outline : {
        color : dlsColor('skyBlue'),
        width : 2
      }
    };

    // the current theme restyles the reshape / transform handles ...
    registerCurrentDrawingUpdates(sketchViewModelRef.current, isCurrent);

    if (!editGeofenceGraphic) {
      // ... and shows its draw-point cursor while the shape is drawn ...
      if (isCurrent) {
        mapViewEl?.classList?.add(GEOFENCE_DRAW_SHAPE_CLASS);
      }
      sketchViewModelRef.current.on(['update', 'undo', 'redo'], onGraphicUpdate);
      // creates and returns an instance of PolygonDrawAction
      // use click mode to prevent freehand draw
      const draw: __esri.Draw = new Draw({
        view : mapView
      });
      drawActionRef.current = draw.create('polygon', {mode: 'click'});

      // listen PolygonDrawAction events to give immediate visual feedback
      // to users as the Polygon is being drawn on the view.
      drawActionRef.current.on(
        [
          'vertex-add',
          'vertex-remove',
          'cursor-update',
          'redo',
          'undo',
          'draw-complete'
        ],
        updateVertices
      );
    } else {
      sketchViewModelRef.current.on(['update', 'undo', 'redo'], onGraphicEditUpdate);
    }

    // Handle double-click event to complete drawing.
    doubleClickHandlerRef.current = mapView.on('double-click', event => {
      if (sketchViewModelRef.current?.state === 'ready') {
        drawActionRef.current?.complete();
      }
    });

    const viewClickHandler = mapView.on('click', function(event) {
      // check if the sketch's state active if it is then that means
      // the graphic is already being updated, no action required.

      event.stopPropagation();
      if (sketchViewModelRef.current?.state === 'active') {
        updateTransformToolLayersGraphicStyles();
      }
    });
    setMapClickHandler(viewClickHandler);

    if (editGeofenceGraphic && sketchViewModelRef.current) {
      editGeofenceGraphic.symbol = DEFAULT_EDIT_SYMBOL;
      geoFencesGraphicsLayerRef.current.add(editGeofenceGraphic);
      sketchViewModelRef.current?.update([editGeofenceGraphic], {tool: 'transform'});
      const {classList} = mapViewEl || {};
      classList?.add?.('default-cursor');
      classList?.remove?.('stop-drawing');
    }

    function updateVertices(event) {
      // create a Polygon from returned vertices
      const {classList} = mapViewEl || {};
      if (event.vertices.length === 2) {
        createGraphic(event);
        classList?.add?.('crosshair-only');
        classList?.remove?.('stop-drawing');
      }
      if (event.vertices.length > 2) {
        createGraphic(event);
        classList?.add?.('stop-drawing');
        classList?.remove?.('crosshair-only');
      }

      if (event.type === 'draw-complete') {
        sketchViewModelRef.current?.update([geoFencesGraphicsLayerRef.current.graphics.items[0]], {tool: 'reshape'});
        setDrawingComplete(true);
        classList?.add?.('default-cursor');
        classList?.remove?.('stop-drawing');
        classList?.remove?.(GEOFENCE_DRAW_SHAPE_CLASS);
      }
    };

    function createGraphic(event) {
      const vertices = event.vertices;
      if (geoFencesGraphicsLayerRef.current) geoFencesGraphicsLayerRef.current.graphics.removeAll();

      const graphic = new Graphic({
        geometry : {
          type             : 'polygon',
          rings            : vertices,
          spatialReference : mapView.spatialReference
        },
        symbol : DEFAULT_SYMBOL()
      });

      geoFencesGraphicsLayerRef.current?.graphics.add(graphic);
      // the current theme marks each placed vertex while drawing; once
      // complete, the reshape tool draws the vertices itself ...
      if (isCurrent && event.type !== 'draw-complete') {
        addVertexMarkers(event);
      }
    };

    function addVertexMarkers(event) {
      // a cursor update's last vertex follows the pointer, not a placed one ...
      const placedVertices = event.type === 'cursor-update' ? event.vertices.slice(0, -1) : event.vertices;
      placedVertices.forEach(function([x, y]) {
        geoFencesGraphicsLayerRef.current?.graphics.add(new Graphic({
          geometry : {
            type             : 'point',
            x,
            y,
            spatialReference : mapView.spatialReference
          },
          symbol : getActivePointSymbol()
        }));
      });
    };

    function onGraphicUpdate(event) {
      if (
        event.toolEventInfo &&
        event.toolEventInfo.type === 'move-stop'
      ) {
        sketchViewModelRef.current?.complete();
      } else if (event.state === 'complete' && geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.update([geoFencesGraphicsLayerRef.current?.graphics?.items[0]], {tool: 'reshape'});
      }
    };

    function onGraphicEditUpdate(event) {
      try {
        if (event.state === 'start' || event.state === 'active' || event.state === 'complete') {
          updateTransformToolLayersGraphicStyles();
        }
      } catch (err) {
        error(err);
      }
    };

    function updateTransformToolLayersGraphicStyles() {
      const transformToolLayer = map.layers.items[map.layers.length - 1];
      transformToolLayer.graphics.items.forEach((graphicsItem) => {
        if (isLegacyMarker(graphicsItem, isCurrent)) {
          graphicsItem.symbol = circleSymbol;
        } else if (graphicsItem.symbol.type === 'simple-fill') {
          graphicsItem.symbol = polygonSymbol;
        }
      });
    }
  };

  return null;
};

export default CreateShapesBoundaries;
